import React, { Suspense, useEffect, useState, useRef } from "react";

interface LazyLoadComponentProps {
  importFunc: () => Promise<{ default: React.ComponentType<any> }>;
  fallback?: React.ReactNode;
  props?: any;
}

const LazyLoadComponent: React.FC<LazyLoadComponentProps> = ({
  importFunc,
  fallback = <div>Loading...</div>,
  props,
}) => {
  const [isVisible, setIsVisible] = useState(false);
  const ComponentRef = useRef<HTMLDivElement | null>(null);
  const [Component, setComponent] = useState<React.ComponentType<any> | null>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.disconnect();
        }
      },
      { threshold: 0.1 }
    );

    if (ComponentRef.current) {
      observer.observe(ComponentRef.current);
    }

    return () => {
      if (ComponentRef.current) {
        observer.unobserve(ComponentRef.current);
      }
    };
  }, []);

  useEffect(() => {
    if (isVisible && !Component) {
      importFunc().then((mod) => setComponent(() => mod.default));
    }
  }, [isVisible]);

  return (
    <div ref={ComponentRef} style={{ minHeight: "100px" }}>
      {Component ? (
        <Suspense fallback={fallback}>
          <Component {...props} />
        </Suspense>
      ) : (
        fallback
      )}
    </div>
  );
};

export default LazyLoadComponent;
